import React, { useState, useEffect } from "react";
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faComments,
  faStar,
  faUsers,
  faCogs,
  faRocket,
  faHandshake,
  faLightbulb,
  faCheckCircle,
  faArrowRight,
  faGem,
  faShieldAlt,
  faHeart,
  faGlobe,
  faAward
} from '@fortawesome/free-solid-svg-icons';

const WhyUs = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const staggerItem = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 },
    transition: { duration: 0.5 }
  };

  const leftServicesData = [
    {
      title: "Online Marketing Experience",
      description: "With 8 years of expertise in Online Marketing, we understand what drives results. We help brands grow, connect, and thrive in the digital world.",
      bgColor: "from-blue-50 to-blue-100",
      color: "from-blue-500 to-blue-600",
      iconBgColor: "bg-blue-600",
      Icon: faCogs,
      features: ["8+ Years Experience", "Digital Growth", "Brand Connection", "Result-Driven"]
    },
    {
      title: "Business Experience",
      description: "With proven expertise across diverse verticals and industries, we bring strategies that adapt to every business landscape.",
      bgColor: "from-orange-50 to-orange-100",
      color: "from-orange-500 to-orange-600",
      iconBgColor: "bg-orange-500",
      Icon: faHandshake,
      features: ["Diverse Industries", "Adaptive Strategies", "Proven Expertise", "Business Growth"]
    },
    {
      title: "Solid Team",
      description: "Our strength lies in a solid team of dedicated full-time employees, backed by specialists who bring deep domain expertise.",
      bgColor: "from-red-50 to-red-100",
      color: "from-red-500 to-red-600",
      iconBgColor: "bg-red-500",
      Icon: faStar,
      features: ["Full-Time Team", "Domain Experts", "Dedicated Staff", "Specialized Skills"]
    },
  ];

  const rightServicesData = [
    {
      title: "Business Strategy",
      description: "We specialize in adapting to diverse business needs, ensuring every strategy delivers real, impactful outcomes.",
      bgColor: "from-green-50 to-green-100",
      color: "from-green-500 to-green-600",
      iconBgColor: "bg-green-600",
      Icon: faChartLine,
      features: ["Strategic Planning", "Impactful Outcomes", "Business Adaptation", "Real Results"]
    },
    {
      title: "Our Passion",
      description: "From ideas to execution, we offer the right strategies and resources to align with your advertising objectives and deliver results.",
      bgColor: "from-yellow-50 to-yellow-100",
      color: "from-yellow-500 to-yellow-600",
      iconBgColor: "bg-yellow-500",
      Icon: faUsers,
      features: ["Ideas to Execution", "Strategic Resources", "Objective Alignment", "Result Delivery"]
    },
    {
      title: "Advertising Plans",
      description: "Discover tools and insights built for your advertising goals, helping you create campaigns that truly make an impact.",
      bgColor: "from-purple-50 to-purple-100",
      color: "from-purple-500 to-purple-600",
      iconBgColor: "bg-purple-500",
      Icon: faRocket,
      features: ["Campaign Tools", "Impact Creation", "Goal Alignment", "Insight-Driven"]
    },
  ];

  const stats = [
    { number: "8+", label: "Years Experience", icon: faAward },
    { number: "500+", label: "Projects Completed", icon: faCheckCircle },
    { number: "99%", label: "Client Satisfaction", icon: faHeart },
    { number: "24/7", label: "Support Available", icon: faGlobe }
  ];


  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-blue-50 pt-20 pb-8 px-2 sm:px-4">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.95 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-7xl mx-auto mb-16"
      >
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12">
          <div className="text-center">
            <div className="inline-block bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
              WHY CHOOSE US
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              What Makes Us <span className="bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">Stand Out</span>
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-blue-600 mx-auto rounded-full mb-6"></div>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover the unique advantages and expertise that make IINSAF the preferred choice for digital marketing and business growth
            </p>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="w-full max-w-7xl mx-auto">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center"
        >
          {/* Left Column */}
          <motion.div
            variants={staggerItem}
            className="space-y-6"
          >
            {leftServicesData.map((service, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className={`bg-gradient-to-br ${service.bgColor} rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group border border-white/20`}
              >
                <div className="flex items-start gap-4">
                  <div className={`${service.iconBgColor} w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}>
                    <FontAwesomeIcon icon={service.Icon} className="text-white text-xl" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                      {service.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {service.features.map((feature, idx) => (
                        <span key={idx} className="text-xs bg-white/50 px-2 py-1 rounded-full text-gray-600">
                          {feature}
                        </span>
                      ))}
              </div>
            </div>
          </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Middle Column - Logo */}
          <motion.div
            variants={staggerItem}
            className="flex justify-center my-8 lg:my-0"
          >
            <div className="relative">
              {/* Outer Ring */}
              <div className="w-48 h-48 relative border-4 border-dashed border-blue-300 rounded-full flex justify-center items-center animate-spin-slow">
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20"></div>
      </div>

              {/* Main Circle */}
              <div className="absolute inset-4 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-2xl">
                <div className="w-40 h-40 bg-gradient-to-br from-blue-700 to-blue-900 rounded-full flex flex-col items-center justify-center text-white text-center p-6">
                  <FontAwesomeIcon icon={faGem} className="text-3xl mb-2 text-yellow-400" />
                  <h2 className="text-lg font-bold">Why Us</h2>
                  <p className="text-xs mt-1 opacity-90">What Makes Us Stand Out</p>
        </div>
      </div>

              {/* Floating Elements */}
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-bounce"></div>
              <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-gradient-to-r from-green-400 to-blue-500 rounded-full animate-bounce animation-delay-100"></div>
              <div className="absolute top-1/2 -left-4 w-3 h-3 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full animate-bounce animation-delay-200"></div>
            </div>
          </motion.div>

      {/* Right Column */}
          <motion.div
            variants={staggerItem}
            className="space-y-6"
          >
            {rightServicesData.map((service, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className={`bg-gradient-to-br ${service.bgColor} rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group border border-white/20`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                      {service.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {service.features.map((feature, idx) => (
                        <span key={idx} className="text-xs bg-white/50 px-2 py-1 rounded-full text-gray-600">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className={`${service.iconBgColor} w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}>
                    <FontAwesomeIcon icon={service.Icon} className="text-white text-xl" />
                  </div>
              </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16"
        >
          <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
                Our <span className="bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">Achievements</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Numbers that showcase our expertise and commitment to excellence
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center group"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <FontAwesomeIcon icon={stat.icon} className="text-white text-xl" />
                  </div>
                  <div className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">{stat.number}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      <style jsx>{`
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
        
        .animation-delay-100 {
          animation-delay: 100ms;
        }
        
        .animation-delay-200 {
          animation-delay: 200ms;
        }
      `}</style>
    </div>
  );
};

export default WhyUs;